import { useCallback, useEffect, useMemo, useRef } from "react";
import { Button, Card, CardBody, CardTitle, Form } from "reactstrap";
import { Action } from "../../../types/reducer-action";
import AccordionContainer from "../../common/accordion-container";
import AutoFocusDiv from "../../common/auto-focus-div";
import BusyButton from "../../common/busy-button";
import ChangeAlert, { ChangeAlertRef } from "../../common/change-alert";
import confirm from "../../common/confirm";
import { DataEntryEvent } from "../../common/data-entry-reducer";
import { CheckIcon, RefreshIcon, SaveIcon } from "../../common/icons";
import AdditionalInfoPanel from "./additional-info-panel";
import AddressInfoPanel from "./address-info-panel";
import { AddressManagerDomain, useAddressDispatch, useAddressState } from "./address-manager-context";
import './style.scss';
import useAddressApi, { isSameAddress } from "./useAddressApi";
import useAddressFormatter from "./useAddressFormatter";

export default function AddressDetailsPane() {
  const { dataSet, selectedDataId, dataDetails, form, subdomains = {} } = useAddressState();
  const dispatch = useAddressDispatch();

  const { [AddressManagerDomain.BusinessEntities]: businessEntityState } = subdomains;
  const { dataSet: businessEntities } = businessEntityState ?? {};

  const { updateAddress, busy } = useAddressApi();

  const [formatAddress] = useAddressFormatter();

  const changeAlertRef = useRef<ChangeAlertRef>(null);

  useEffect(() => {
    !!selectedDataId && dispatch(new Action(DataEntryEvent.SetDataDetails,
      dataSet?.find(item => item.id === selectedDataId)
    ));
  }, [dataSet, selectedDataId]);

  const reset = () => dispatch(new Action(DataEntryEvent.ResetForm));

  const isDirty = useMemo(() => (
    form?.expired !== dataDetails?.expired || !isSameAddress(dataDetails, form)
  ), [dataDetails, form]);

  const verify = useCallback(async () => {
    if (!form) {
      return false;
    } else if (form.expired && !dataDetails?.expired) {
      return confirm({
        title: 'Warning',
        content: `This address will be expired. There are ${businessEntities?.length ?? 0} records associated with this address.`,
        okText: <><CheckIcon/> Confirm </>,
        okBtnColor: 'warning',
        titleCenter: true,
        bodyCenter: true
      }).then(() => true);
    } else {
      return true;
    }
  }, [dataDetails, form]);

  const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!selectedDataId) return;

    verify()
      .then(ok => ok && updateAddress(selectedDataId, form)
        .then(success => changeAlertRef.current?.complete(success))
        .catch((e: Error) => confirm({
          title: 'Duplicated address',
          content: e.message,
          okText: '', // hide OK button
          cancelText: 'OK',
          titleCenter: true,
          bodyCenter: true
        }))
      );
  }, [selectedDataId, form]);

  const handleReset = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    reset();
  }

  if (!dataDetails) return <></>;

  return (
    <Card>
      <CardBody>
        <CardTitle>
          <AutoFocusDiv trigger={dataDetails}>
            Address: {formatAddress(dataDetails, ', ')}
          </AutoFocusDiv>
        </CardTitle>

        <ChangeAlert isOpen={isDirty}
          itemName={`address "${formatAddress(form)}"`}
          onDiscard={reset}
          ref={changeAlertRef} />

        <Form className="oms-container" onSubmit={handleSubmit} onReset={handleReset}>
          <div className="oms-row">
            <BusyButton type="submit" aria-label="Save" color="primary"
              disabled={!isDirty}
              value="Save" busy={busy}>
              <SaveIcon />
            </BusyButton>

            <Button type="reset" aria-label="Reset" color="secondary"
              disabled={busy} >
              <RefreshIcon /> Reset
            </Button>
          </div>

          <AccordionContainer title="Address Information" titleClass="fs-6">
            <AddressInfoPanel wide />
          </AccordionContainer>

          <AccordionContainer title="Additional Information" titleClass="fs-6">
            <AdditionalInfoPanel wide />
          </AccordionContainer>
        </Form>
      </CardBody>
    </Card>
  );
}
